import React, { useState } from "react";
import { Notebook } from "../types";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PendingIcon from "@mui/icons-material/Pending";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import { applyRagToNotebook, getRagStatus } from "../services/api";

interface IRagButtonProps {
  notebook: Notebook;
  isEligibleForRag: boolean;
  onError: (message: string) => void;
  removeEligibilityForRag: () => void;
  displayRagSuccessMessage: (message: string) => void;
  onChangeRagStatus: (status: string) => void;
}

const RagButton: React.FC<IRagButtonProps> = ({
  notebook,
  isEligibleForRag,
  onChangeRagStatus,
  onError,
  removeEligibilityForRag,
  displayRagSuccessMessage,
}) => {
  const [isApplyingRag, setIsApplyingRag] = useState<boolean>(false);

  const getRagStatusIcon = (notebook: Notebook) => {
    const ragStatus = notebook.ragStatus || "not_started";

    if (ragStatus === "done") {
      return (
        <Tooltip title="RAG успешно применен">
          <CheckCircleIcon color="success" />
        </Tooltip>
      );
    } else if (ragStatus === "error") {
      return (
        <Tooltip title="Ошибка при применении RAG">
          <ErrorIcon color="error" />
        </Tooltip>
      );
    } else if (ragStatus === "processing") {
      return (
        <Tooltip title="RAG в процессе применения">
          <HourglassEmptyIcon color="info" />
        </Tooltip>
      );
    } else {
      return (
        <Tooltip title="RAG не применялся">
          <PendingIcon color="disabled" />
        </Tooltip>
      );
    }
  };

  const getRagTooltip = (notebook: Notebook) => {
    if (canApplyRag(notebook)) {
      return "Сгенерировать комментарии с помощью RAG";
    } else {
      return "RAG недоступен для этого ноутбука";
    }
  };

  const canApplyRag = (notebook: Notebook) => {
    // Если ноутбук в списке eligible и его статус not_started или error, то можно применить RAG
    if (isEligibleForRag) {
      const ragStatus = notebook.ragStatus || "not_started";
      return ragStatus === "not_started" || ragStatus === "error";
    }

    return false;
  };

  const getRagIconColor = (notebook: Notebook) => {
    if (canApplyRag(notebook)) {
      return "primary";
    } else {
      return "disabled";
    }
  };

  const handleApplyRag = async () => {
    setIsApplyingRag(true);
    const notebookId = notebook.id;
    try {
      await applyRagToNotebook(notebookId);

      displayRagSuccessMessage("Задача по применению RAG запущена");

      // Обновляем статус RAG для этого ноутбука
      onChangeRagStatus("processing");

      removeEligibilityForRag();

      // Периодически проверяем статус RAG
      const checkRagStatus = async () => {
        try {
          const status = await getRagStatus(notebookId);
          if (status.status === "done" || status.status === "error") {
            // Если статус изменился на done или error, обновляем его и останавливаем проверку
            onChangeRagStatus(status.status);
            return;
          }

          // Если статус все еще processing, продолжаем проверять
          setTimeout(checkRagStatus, 5000);
        } catch (err) {
          console.error(
            `Ошибка при получении статуса RAG для ноутбука ${notebookId}:`,
            err
          );
        }
      };

      // Запускаем проверку статуса через 5 секунд
      setTimeout(checkRagStatus, 5000);
    } catch (err) {
      console.error("Ошибка при запуске RAG:", err);
      onError("Не удалось запустить RAG");
    } finally {
      setIsApplyingRag(false);
    }
  };

  return (
    <Box display="flex" alignItems={"center"} gap={1}>
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 24,
          height: 24,
          verticalAlign: "middle",
        }}
      >
        {getRagStatusIcon(notebook)}
      </Box>

      <Box minWidth={24}>
        <Tooltip title={getRagTooltip(notebook)}>
          <IconButton
            edge="end"
            aria-label="apply-rag"
            onClick={handleApplyRag}
            disabled={!canApplyRag(notebook) || isApplyingRag}
            sx={{ px: 0 }}
          >
            {isApplyingRag ? (
              <CircularProgress size={20} />
            ) : (
              <PlayArrowIcon color={getRagIconColor(notebook)} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default RagButton;
